import { useEffect, useRef, useState } from "react";
import AustenGame from "./AustenGame";
import RewardBox from "./RewardBox";
import audio from "../game/audio";
import "./GameShell.css";
import { Download, Share2, Smartphone, X, Check, Gift, Volume2, VolumeX } from "lucide-react";

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const SHARE_TEXT =
  "Ho giocato alla demo ufficiale de Il mondo di Jane Austen! Aiutiamo Land Editore a realizzare il videogioco completo: gioca e condividi anche tu.";

export default function GameShell() {
  const [phase, setPhase] = useState("splash"); // splash | menu | game
  const [installEvt, setInstallEvt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIos, setShowIos] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [toast, setToast] = useState(null);
  const splashTimer = useRef(null);

  const iOS = isIOS();
  const [showReward, setShowReward] = useState(false);
  const missionCompleted =
    (() => { try { return localStorage.getItem("austen_quest_v1") === "completed"; } catch { return false; } })();
  const shareUrl = window.location.origin + window.location.pathname;

  // Splash auto-advance (~2.5s). Shown once per session (page load), NOT on
  // in-game mission restart (that lives inside AustenGame).
  useEffect(() => {
    if (phase !== "splash") return;
    splashTimer.current = setTimeout(() => setPhase("menu"), 2500);
    return () => clearTimeout(splashTimer.current);
  }, [phase]);

  // PWA install prompt capture
  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); setInstallEvt(e); };
    const onInstalled = () => { setInstalled(true); setInstallEvt(null); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Audio is enabled explicitly via the menu buttons (respecting the user's
  // choice), so there is no silent auto-unlock on stray taps.

  const skipSplash = () => {
    if (phase === "splash") {
      clearTimeout(splashTimer.current);
      setPhase("menu");
    }
  };

  // Start WITH audio: unlock the AudioContext directly inside this gesture.
  const playWithAudio = () => {
    audio.setMuted(false);
    audio.unlock();
    setPhase("game");
  };
  // Start WITHOUT audio: keep music & effects off.
  const playMuted = () => {
    audio.setMuted(true);
    setPhase("game");
  };

  const doInstall = async () => {
    audio.unlock();
    if (installEvt) {
      installEvt.prompt();
      try {
        const { outcome } = await installEvt.userChoice;
        if (outcome === "accepted") setInstalled(true);
      } catch {}
      setInstallEvt(null);
      return;
    }
    if (iOS) setShowIos(true);
  };

  const copyLink = async (msg) => {
    const full = `${SHARE_TEXT} ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(full);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = full;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
    setToast(msg);
    setTimeout(() => setToast(null), 3400);
  };

  const openShare = () => setShowShare(true);

  const shareWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT + " " + shareUrl)}`,
      "_blank", "noopener"
    );
  };
  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank", "noopener"
    );
  };
  const shareMessenger = () => {
    // Best-effort deep link on mobile; always copy so there is a real action.
    if (/iphone|ipad|ipod|android/i.test(navigator.userAgent)) {
      try { window.location.href = `fb-messenger://share?link=${encodeURIComponent(shareUrl)}`; } catch {}
    }
    copyLink("Link copiato! Apri Messenger e incollalo nella conversazione.");
  };
  const shareEmail = () => {
    const subject = "Devi provare questo videogioco di Jane Austen!";
    const body =
      "Ho giocato alla demo ufficiale de Il mondo di Jane Austen. Questa è soltanto la prima avventura: se avrà abbastanza condivisioni, Land Editore realizzerà il videogioco completo. Provalo qui: " +
      shareUrl;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Il mondo di Jane Austen – Demo",
          text: SHARE_TEXT,
          url: shareUrl,
        });
        return;
      } catch { return; }
    }
    copyLink("Link copiato! Condividilo ovunque.");
  };
  const shareCopy = () => copyLink("Link copiato! Condividilo ovunque.");

  const installAvailable = !!installEvt || iOS;
  const installLabel = installed
    ? "Demo già installata"
    : "SCARICA LA DEMO";

  const Corners = () => (
    <>
      <img className="corner c-tl" src="/assets/corner_ornament.png" alt="" aria-hidden="true" />
      <img className="corner c-tr" src="/assets/corner_ornament.png" alt="" aria-hidden="true" />
      <img className="corner c-bl" src="/assets/corner_ornament.png" alt="" aria-hidden="true" />
      <img className="corner c-br" src="/assets/corner_ornament.png" alt="" aria-hidden="true" />
    </>
  );

  if (phase === "game") return <AustenGame />;

  return (
    <div className="shell-root" data-testid="game-shell">
      {phase === "splash" && (
        <div
          className="splash"
          data-testid="splash-screen"
          onClick={skipSplash}
          onTouchStart={skipSplash}
        >
          <Corners />
          <div className="splash-inner">
            <div className="splash-brand" data-testid="splash-brand">
              <span className="brand-line">LAND EDITORE</span>
              <span className="brand-presents">PRESENTA</span>
            </div>
            <div className="gold-divider" />
            <p className="splash-sub">Un'avventura nel mondo di Jane Austen</p>
          </div>
          <span className="splash-skip">tocca per saltare</span>
        </div>
      )}

      {phase === "menu" && (
        <div className="menu" data-testid="main-menu">
          <div className="menu-frame">
            <Corners />
            <div className="menu-scroll">
              <div className="menu-eyebrow">Land Editore presenta</div>
              <h1 className="menu-title" data-testid="menu-title">IL MONDO DI JANE AUSTEN</h1>
              <div className="menu-demo-badge" data-testid="menu-demo-badge">
                DEMO UFFICIALE DEL VIDEOGIOCO
              </div>
              {missionCompleted && (
                <div className="menu-completed-badge" data-testid="menu-completed-badge">
                  ✦ MISSIONE COMPLETATA
                </div>
              )}
              <div className="gold-divider narrow" />

              <p className="menu-lead" data-testid="menu-lead">
                Questa è soltanto la prima avventura.
              </p>
              <p className="menu-body">
                Aiutateci a realizzare il videogioco completo: giocate, scaricate la
                demo e condividetela <span className="hl-ovunque">OVUNQUE</span>.
              </p>
              <p className="menu-body">
                Più lettori entreranno nel mondo di Jane Austen, più grande potremo
                renderlo.
              </p>

              <div className="menu-buttons">
                <button className="menu-btn primary" onClick={playWithAudio} data-testid="play-btn">
                  <Volume2 size={20} /> ATTIVA AUDIO E GIOCA
                </button>
                <button className="menu-btn muted-play" onClick={playMuted} data-testid="play-muted-btn">
                  <VolumeX size={18} /> GIOCA SENZA AUDIO
                </button>
                <button
                  className="menu-btn gold"
                  onClick={doInstall}
                  disabled={installed || !installAvailable}
                  data-testid="install-btn"
                >
                  <Download size={20} /> {installLabel}
                </button>
                <button className="menu-btn wine" onClick={openShare} data-testid="share-btn">
                  <Share2 size={20} /> CONDIVIDI OVUNQUE
                </button>
                {missionCompleted && (
                  <button className="menu-btn gold" onClick={() => setShowReward(true)} data-testid="review-reward-btn">
                    <Gift size={20} /> RIVEDI IL PREMIO
                  </button>
                )}
              </div>

              <div className="menu-foot">Land Editore · Demo</div>
            </div>
          </div>
        </div>
      )}

      {/* Share modal */}
      {showShare && (
        <div className="share-overlay" data-testid="share-modal" onClick={() => setShowShare(false)}>
          <div className="share-card" onClick={(e) => e.stopPropagation()}>
            <button className="ios-close" onClick={() => setShowShare(false)} data-testid="share-close" aria-label="Chiudi">
              <X size={20} />
            </button>
            <h2 className="share-title">Condividi la prima avventura</h2>
            <p className="share-text">
              Aiutaci a realizzare il videogioco completo: condividi la demo con
              tutti gli amanti di Jane Austen.
            </p>
            <div className="share-grid">
              <button className="share-item wa" onClick={shareWhatsApp} data-testid="share-whatsapp">
                <i className="fa-brands fa-whatsapp" aria-hidden="true"></i>
                <span>WhatsApp</span>
              </button>
              <button className="share-item fb" onClick={shareFacebook} data-testid="share-facebook">
                <i className="fa-brands fa-facebook-f" aria-hidden="true"></i>
                <span>Facebook</span>
              </button>
              <button className="share-item ms" onClick={shareMessenger} data-testid="share-messenger">
                <i className="fa-brands fa-facebook-messenger" aria-hidden="true"></i>
                <span>Messenger</span>
              </button>
              <button className="share-item em" onClick={shareEmail} data-testid="share-email">
                <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                <span>Email</span>
              </button>
              <button className="share-item nt" onClick={shareNative} data-testid="share-native">
                <i className="fa-solid fa-share-nodes" aria-hidden="true"></i>
                <span>Altre app</span>
              </button>
              <button className="share-item cp" onClick={shareCopy} data-testid="share-copy">
                <i className="fa-solid fa-link" aria-hidden="true"></i>
                <span>Copia link</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iOS install instructions */}
      {showIos && (
        <div className="ios-overlay" data-testid="ios-instructions" onClick={() => setShowIos(false)}>
          <div className="ios-card" onClick={(e) => e.stopPropagation()}>
            <button className="ios-close" onClick={() => setShowIos(false)} data-testid="ios-close" aria-label="Chiudi">
              <X size={20} />
            </button>
            <Smartphone size={40} className="ios-phone" />
            <h2>Aggiungi alla schermata Home</h2>
            <p>Per installare la demo su iPhone o iPad:</p>
            <ol className="ios-steps">
              <li>
                <span className="ios-step-ico"><Share2 size={18} /></span>
                Tocca il pulsante <b>Condividi</b> nella barra di Safari.
              </li>
              <li>
                <span className="ios-step-ico"><Check size={18} /></span>
                Scorri e scegli <b>“Aggiungi alla schermata Home”</b>.
              </li>
              <li>
                <span className="ios-step-ico"><Check size={18} /></span>
                Conferma con <b>Aggiungi</b>: l'icona apparirà sulla Home.
              </li>
            </ol>
          </div>
        </div>
      )}

      {showReward && (
        <div className="ios-overlay" data-testid="reward-modal" onClick={() => setShowReward(false)}>
          <div className="ios-card reward-card" onClick={(e) => e.stopPropagation()}>
            <button className="ios-close" onClick={() => setShowReward(false)} data-testid="reward-close" aria-label="Chiudi">
              <X size={20} />
            </button>
            <h2 className="reward-modal-title">Il mio premio</h2>
            <RewardBox />
          </div>
        </div>
      )}

      {toast && (
        <div className="shell-toast" data-testid="share-toast">{toast}</div>
      )}
    </div>
  );
}
